import React from 'react';
import styled, { keyframes } from 'styled-components';
import WorkspaceMessageBody from '../WorkspaceMessageBody';
import type { TurnStatus } from '../../../hooks/useWorkspace';

const Wrap = styled.div`
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--text-primary);
  word-break: break-word;
`;

const caretBlink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

const StreamingCaret = styled.span`
  display: inline-block;
  width: 6px;
  height: 1em;
  margin-left: 2px;
  background: var(--accent, #6366f1);
  vertical-align: middle;
  animation: ${caretBlink} 1s step-end infinite;
`;

const ErrorBlock = styled.div`
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  background: var(--error-subtle, #fef2f2);
  border: 1px solid #fecaca;
  color: var(--error, #ef4444);
  font-size: 0.8125rem;
`;

interface AssistantResponseProps {
  content: string;
  status: TurnStatus;
  errorSummary?: string;
}

/**
 * Renders the assistant's response for a turn. Uses the shared
 * {@link WorkspaceMessageBody} markdown renderer so code fences / lists / etc
 * behave identically to the previous bubble implementation, but without
 * bubble chrome — the turn document frames the response instead.
 */
const AssistantResponse: React.FC<AssistantResponseProps> = ({
  content,
  status,
  errorSummary,
}) => {
  if (status === 'failed' && errorSummary) {
    return <ErrorBlock>{errorSummary}</ErrorBlock>;
  }

  if (!content && status === 'running') {
    return (
      <Wrap>
        <StreamingCaret aria-hidden="true" />
      </Wrap>
    );
  }

  if (!content) return null;

  return (
    <Wrap>
      <WorkspaceMessageBody content={content} />
      {status === 'running' && <StreamingCaret aria-hidden="true" />}
    </Wrap>
  );
};

export default AssistantResponse;
